import { useCallback, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RotateCcwIcon, BookOpenIcon } from "lucide-react";
import type { HLDContent, HLDHiddenAnswer } from "@/types/assessment";
import { MarkdownEditor } from "./markdown-editor";
import { RubricPanel } from "./rubric-panel";
import { MarkdownContent } from "@/components/ui/markdown-content";

interface HLDInputProps {
  content: HLDContent;
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  showResults?: boolean;
  hiddenAnswer?: HLDHiddenAnswer;
  vimEnabled?: boolean;
  onVimToggle?: (enabled: boolean) => void;
}

export function HLDInput({
  content,
  value,
  onChange,
  disabled = false,
  showResults = false,
  hiddenAnswer,
  vimEnabled = false,
  onVimToggle,
}: HLDInputProps) {
  // Get the current text (value or template)
  const currentText = value ?? content.template;

  // Check if content has been modified from template
  const hasModifications = currentText !== content.template;

  // Initialize value on mount if not set
  useEffect(() => {
    if (value === null) {
      onChange(content.template);
    }
    // Only run on mount - template changes shouldn't reset user input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle reset to original template
  const handleReset = useCallback(() => {
    onChange(content.template);
  }, [content.template, onChange]);

  return (
    <div className="space-y-4">
      {/* Controls row */}
      <div className="flex items-center justify-between">
        {/* Reset button */}
        {!disabled && hasModifications && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="text-muted-foreground"
          >
            <RotateCcwIcon className="size-4" />
            Reset to Template
          </Button>
        )}

        {/* Spacer when no reset button */}
        {(!hasModifications || disabled) && <div />}

        {/* Vim toggle */}
        <div className="flex items-center gap-2">
          <Switch
            id="vim-mode-hld"
            checked={vimEnabled}
            onCheckedChange={(checked) => onVimToggle?.(checked)}
            disabled={disabled}
          />
          <Label
            htmlFor="vim-mode-hld"
            className="text-sm text-muted-foreground cursor-pointer"
          >
            Vim
          </Label>
        </div>
      </div>

      {/* Main content area: Editor + Rubric sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* Markdown editor */}
        <MarkdownEditor
          value={currentText}
          onChange={onChange}
          disabled={disabled}
          minHeight="400px"
          placeholder="Start writing your high-level design..."
          vimEnabled={vimEnabled}
        />

        {/* Rubric sidebar */}
        <RubricPanel rubric={content.rubric} />
      </div>

      {/* Example solution panel (after submission) */}
      {showResults && hiddenAnswer?.example_hld && (
        <HLDExamplePanel exampleHLD={hiddenAnswer.example_hld} />
      )}
    </div>
  );
}

// Example solution panel shown after submission
function HLDExamplePanel({ exampleHLD }: { exampleHLD: string }) {
  return (
    <div className="space-y-3 p-4 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900">
      {/* Header */}
      <div className="flex items-center gap-2">
        <BookOpenIcon className="size-5 text-blue-600 dark:text-blue-400" />
        <span className="font-medium text-blue-800 dark:text-blue-300">
          Example Solution
        </span>
      </div>

      {/* Example HLD content */}
      <MarkdownContent>{exampleHLD}</MarkdownContent>
    </div>
  );
}
